import { useEffect, useState } from "react";
import { Container, Row, Col, Table, Alert, Badge, Form, Button, Spinner } from "react-bootstrap";
import { Search, PersonFillAdd, PersonFillDash } from "react-bootstrap-icons";
import SubsiteService from "../../../services/subsite.service";
import UserService from "../../../services/user.service";

const AdminSuperAdmins = () => {
  const [superAdmins, setSuperAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Search for a user to promote (global)
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);

  // Subsite-specific assignment
  const [subsites, setSubsites] = useState([]);
  const [subSearchInput, setSubSearchInput] = useState("");
  const [subSearchResults, setSubSearchResults] = useState([]);
  const [subSearching, setSubSearching] = useState(false);
  const [selectedSubsiteId, setSelectedSubsiteId] = useState("");
  const [subAdding, setSubAdding] = useState(null);

  useEffect(() => {
    loadSuperAdmins();
    loadSubsites();
  }, []);

  const loadSubsites = async () => {
    try {
      const data = await SubsiteService.getAllSubsites();
      setSubsites(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      // non-critical, ignore
    }
  };

  const loadSuperAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await SubsiteService.getSuperAdmins();
      setSuperAdmins(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to load super admins");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchInput.trim()) return;
    try {
      setSearching(true);
      setError(null);
      const response = await UserService.searchAllUsers([1, 10], searchInput.trim());
      setSearchResults(response.data || []);
    } catch (err) {
      setError("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleAdd = async (user) => {
    try {
      setAdding(user.id);
      setError(null);
      await SubsiteService.addSuperAdmin(user.id);
      setSuccess(`${user.firstname} ${user.lastname} has been added as a Super Admin and linked to all active subsites.`);
      setSearchResults([]);
      setSearchInput("");
      await loadSuperAdmins();
    } catch (err) {
      setError("Failed to add super admin: " + (err.response?.data?.message || err.message));
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (user) => {
    if (!window.confirm(`Remove super admin role from ${user.firstname} ${user.lastname} on ALL subsites?`)) return;
    try {
      setError(null);
      await SubsiteService.removeSuperAdminRole(user.id);
      setSuccess(`Super admin role removed from ${user.firstname} ${user.lastname}.`);
      await loadSuperAdmins();
    } catch (err) {
      setError("Failed to remove super admin: " + (err.response?.data?.message || err.message));
    }
  };

  const handleSubSearch = async (e) => {
    e.preventDefault();
    if (!subSearchInput.trim()) return;
    try {
      setSubSearching(true);
      setError(null);
      const response = await UserService.searchAllUsers([1, 10], subSearchInput.trim());
      setSubSearchResults(response.data || []);
    } catch (err) {
      setError("Search failed");
    } finally {
      setSubSearching(false);
    }
  };

  const handleAddToSubsite = async (user) => {
    if (!selectedSubsiteId) {
      setError("Please select a subsite first.");
      return;
    }
    const subsite = subsites.find(s => String(s.id) === String(selectedSubsiteId));
    try {
      setSubAdding(user.id);
      setError(null);
      await SubsiteService.addSuperAdminToSubsite(user.id, selectedSubsiteId);
      setSuccess(`${user.firstname} ${user.lastname} has been added as Super Admin on ${subsite?.subsiteName || 'the selected subsite'}.`);
      setSubSearchResults([]);
      setSubSearchInput("");
      setSelectedSubsiteId("");
      await loadSuperAdmins();
    } catch (err) {
      setError("Failed to add super admin to subsite: " + (err.response?.data?.message || err.message));
    } finally {
      setSubAdding(null);
    }
  };

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col><h3>Super Admin Management</h3></Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Current Super Admins */}
      <Row className="mb-4">
        <Col>
          <h5>Current Super Admins</h5>
          {loading ? (
            <Spinner animation="border" />
          ) : superAdmins.length === 0 ? (
            <p className="text-muted">No super admins found.</p>
          ) : (
            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Username</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {superAdmins.map(u => (
                  <tr key={u.id}>
                    <td>{u.firstname} {u.lastname} <Badge bg="danger">Super</Badge></td>
                    <td>{u.email}</td>
                    <td>{u.username}</td>
                    <td>
                      <Button size="sm" variant="outline-danger" onClick={() => handleRemove(u)}>
                        <PersonFillDash /> Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>

      {/* Add Super Admin */}
      <Row className="mb-4">
        <Col md={8}>
          <div className="border rounded p-3 bg-light">
            <h5>Add Super Admin</h5>
            <p className="text-muted small">Search for a user and grant them the super admin role on all active subsites.</p>
            <Form onSubmit={handleSearch} className="d-flex gap-2 mb-2">
              <Form.Control
                type="text"
                placeholder="Search by name or email..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
              <Button type="submit" variant="primary" disabled={searching}>
                {searching ? <Spinner size="sm" animation="border" /> : <Search />}
              </Button>
            </Form>
            {searchResults.length > 0 && (
              <Table size="sm" bordered>
                <thead><tr><th>Name</th><th>Email</th><th></th></tr></thead>
                <tbody>
                  {searchResults.map(u => (
                    <tr key={u.id}>
                      <td>{u.firstname} {u.lastname}</td>
                      <td>{u.email}</td>
                      <td>
                        <Button size="sm" variant="success" disabled={adding === u.id} onClick={() => handleAdd(u)}>
                          {adding === u.id ? <Spinner size="sm" animation="border" /> : <><PersonFillAdd /> Add</>}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </Col>
      </Row>

      {/* Add Super Admin to a Specific Subsite */}
      <Row className="mb-4">
        <Col md={8}>
          <div className="border rounded p-3 bg-light">
            <h5>Add Super Admin to a Specific Subsite</h5>
            <p className="text-muted small">Grant the super admin role on one subsite only — useful for site managers.</p>
            <Form.Group className="mb-2">
              <Form.Label className="small fw-semibold">Select Subsite</Form.Label>
              <Form.Select
                value={selectedSubsiteId}
                onChange={e => setSelectedSubsiteId(e.target.value)}
              >
                <option value="">— Choose a subsite —</option>
                {subsites.map(s => (
                  <option key={s.id} value={s.id}>{s.subsiteName}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form onSubmit={handleSubSearch} className="d-flex gap-2 mb-2">
              <Form.Control
                type="text"
                placeholder="Search by name or email..."
                value={subSearchInput}
                onChange={e => setSubSearchInput(e.target.value)}
              />
              <Button type="submit" variant="primary" disabled={subSearching}>
                {subSearching ? <Spinner size="sm" animation="border" /> : <Search />}
              </Button>
            </Form>
            {subSearchResults.length > 0 && (
              <Table size="sm" bordered>
                <thead><tr><th>Name</th><th>Email</th><th></th></tr></thead>
                <tbody>
                  {subSearchResults.map(u => (
                    <tr key={u.id}>
                      <td>{u.firstname} {u.lastname}</td>
                      <td>{u.email}</td>
                      <td>
                        <Button size="sm" variant="success" disabled={subAdding === u.id} onClick={() => handleAddToSubsite(u)}>
                          {subAdding === u.id ? <Spinner size="sm" animation="border" /> : <><PersonFillAdd /> Add to Subsite</>}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminSuperAdmins;
